/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Form,
  Icon,
  Message,
  Card,
} from 'semantic-ui-react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  API,
  copy,
  showError,
  showSuccess,
  timestamp2string,
} from '../../helpers';
import { renderQuotaWithPrompt } from '../../helpers/render';

const TASK_TYPE_OPTIONS = [
  { key: 'image', text: '生图', value: 'image' },
  { key: 'video', text: '视频', value: 'video' },
];

const normalizeConcurrencyLimits = (value) => {
  if (!value) {
    return [];
  }
  if (Array.isArray(value)) {
    return value;
  }
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const EditToken = () => {
  const { t } = useTranslation();
  const params = useParams();
  const tokenId = params.id;
  const isEdit = tokenId !== undefined;
  const [loading, setLoading] = useState(isEdit);
  const [modelOptions, setModelOptions] = useState([]);
  const [channelOptions, setChannelOptions] = useState([]);
  const originInputs = {
    name: '',
    project_number: '',
    remain_quota: isEdit ? 0 : 500000,
    expired_time: -1,
    unlimited_quota: false,
    models: [],
    subnet: '',
    channel_filter_mode: 'off',
    channel_filter_values: [],
    model_concurrency_limits: [],
    remain_quota_usd: null,
    remain_quota_cny: null,
  };
  const [inputs, setInputs] = useState(originInputs);
  const { name, project_number, remain_quota, expired_time, unlimited_quota, remain_quota_usd, remain_quota_cny } = inputs;
  const navigate = useNavigate();
  const handleInputChange = (e, { name, value }) => {
    setInputs((inputs) => ({ ...inputs, [name]: value }));
  };
  const handleCancel = () => {
    navigate('/token');
  };
  const setExpiredTime = (month, day, hour, minute) => {
    let now = new Date();
    let timestamp = now.getTime() / 1000;
    let seconds = month * 30 * 24 * 60 * 60;
    seconds += day * 24 * 60 * 60;
    seconds += hour * 60 * 60;
    seconds += minute * 60;
    if (seconds !== 0) {
      timestamp += seconds;
      setInputs({ ...inputs, expired_time: timestamp2string(timestamp) });
    } else {
      setInputs({ ...inputs, expired_time: -1 });
    }
  };

  const setUnlimitedQuota = () => {
    setInputs({ ...inputs, unlimited_quota: !unlimited_quota });
  };

  // --- Model Concurrency Limits helpers ---
  const addConcurrencyRule = () => {
    setInputs((prev) => ({
      ...prev,
      model_concurrency_limits: [
        ...prev.model_concurrency_limits,
        { task_type: 'image', model: '', limit: 1 },
      ],
    }));
  };

  const removeConcurrencyRule = (index) => {
    setInputs((prev) => ({
      ...prev,
      model_concurrency_limits: prev.model_concurrency_limits.filter(
        (_, i) => i !== index
      ),
    }));
  };

  const updateConcurrencyRule = (index, field, value) => {
    setInputs((prev) => {
      const rules = [...prev.model_concurrency_limits];
      rules[index] = { ...rules[index], [field]: value };
      return { ...prev, model_concurrency_limits: rules };
    });
  };

  const loadToken = async () => {
    try {
      let res = await API.get(`/api/token/${tokenId}`);
      const { success, message, data } = res.data || {};
      if (success && data) {
        if (data.expired_time !== -1) {
          data.expired_time = timestamp2string(data.expired_time);
        }
        if (data.models === '') {
          data.models = [];
        } else {
          data.models = data.models.split(',');
        }
        if (!data.channel_filter_values) {
          data.channel_filter_values = [];
        } else {
          data.channel_filter_values = data.channel_filter_values
            .split(',')
            .map((id) => parseInt(id, 10))
            .filter((id) => !Number.isNaN(id));
        }
        data.channel_filter_mode = data.channel_filter_mode || 'off';
        data.model_concurrency_limits = normalizeConcurrencyLimits(
          data.model_concurrency_limits
        );
        setInputs(data);
      } else {
        showError(message || 'Failed to load token');
      }
    } catch (error) {
      showError(error.message || 'Network error');
    }
    setLoading(false);
  };

  const loadAvailableModels = async () => {
    try {
      let res = await API.get(`/api/user/available_models`);
      const { success, message, data } = res.data || {};
      if (success && data) {
        let options = data.map((model) => {
          return {
            key: model,
            text: model,
            value: model,
          };
        });
        setModelOptions(options);
      } else {
        showError(message || 'Failed to load models');
      }
    } catch (error) {
      showError(error.message || 'Network error');
    }
  };

  const loadAvailableChannels = async () => {
    try {
      let res = await API.get(`/api/user/available_channels`);
      const { success, message, data } = res.data || {};
      if (success && data) {
        let options = data.map((channel) => ({
          key: channel.id,
          text: `#${channel.id} ${channel.name || t('token.edit.channel_filter.channel_without_name')}`,
          value: channel.id,
        }));
        setChannelOptions(options);
      } else {
        showError(message || 'Failed to load channels');
      }
    } catch (error) {
      showError(error.message || 'Network error');
    }
  };

  // Token editor loads route-bound data once on mount.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isEdit) {
      loadToken().catch((error) => {
        showError(error.message || 'Failed to load token');
        setLoading(false);
      });
    }
    loadAvailableModels().catch((error) => {
      showError(error.message || 'Failed to load models');
    });
    loadAvailableChannels().catch((error) => {
      showError(error.message || 'Failed to load channels');
    });
  }, []);

  const submit = async () => {
    if (!isEdit && inputs.name === '') return;
    let localInputs = { ...inputs };
    localInputs.remain_quota = parseInt(localInputs.remain_quota);

    // 处理双币种额度字段（空字符串转为null）
    if (localInputs.remain_quota_usd === '' || localInputs.remain_quota_usd === null) {
      localInputs.remain_quota_usd = null;
    } else {
      localInputs.remain_quota_usd = parseFloat(localInputs.remain_quota_usd);
    }

    if (localInputs.remain_quota_cny === '' || localInputs.remain_quota_cny === null) {
      localInputs.remain_quota_cny = null;
    } else {
      localInputs.remain_quota_cny = parseFloat(localInputs.remain_quota_cny);
    }

    if (localInputs.expired_time !== -1) {
      let time = Date.parse(localInputs.expired_time);
      if (isNaN(time)) {
        showError(t('token.edit.messages.expire_time_invalid'));
        return;
      }
      localInputs.expired_time = Math.ceil(time / 1000);
    }
    localInputs.models = localInputs.models.join(',');
    localInputs.channel_filter_values = (localInputs.channel_filter_values || []).join(',');

    // Validate and serialize model_concurrency_limits
    const rules = normalizeConcurrencyLimits(
      localInputs.model_concurrency_limits
    );
    const seen = new Set();
    for (const rule of rules) {
      if (!rule.model || !rule.model.trim()) {
        showError('模型并发限制中模型名称不能为空');
        return;
      }
      const limit = parseInt(rule.limit, 10);
      if (!Number.isInteger(limit) || limit <= 0) {
        showError('并发限制必须为正整数');
        return;
      }
      const key = `${rule.task_type}:${rule.model.trim()}`;
      if (seen.has(key)) {
        showError('同一任务类型和模型只能配置一条并发规则');
        return;
      }
      seen.add(key);
    }
    localInputs.model_concurrency_limits = rules.length > 0
      ? JSON.stringify(rules.map((r) => ({
          task_type: r.task_type,
          model: r.model.trim(),
          limit: parseInt(r.limit, 10),
        })))
      : '';

    let res;
    if (isEdit) {
      res = await API.put(`/api/token/`, {
        ...localInputs,
        id: parseInt(tokenId),
      });
    } else {
      res = await API.post(`/api/token/`, localInputs);
    }
    const { success, message } = res.data;
    if (success) {
      if (isEdit) {
        showSuccess(t('token.edit.messages.update_success'));
      } else {
        showSuccess(t('token.edit.messages.create_success'));
        setInputs(originInputs);
      }
    } else {
      showError(message);
    }
  };

  return (
    <div className='dashboard-container'>
      <Card fluid className='chart-card'>
        <Card.Content>
          <Card.Header className='header'>
            {isEdit ? t('token.edit.title_edit') : t('token.edit.title_create')}
          </Card.Header>
          <Form loading={loading} autoComplete='new-password'>
            <Form.Field>
              <Form.Input
                label={t('token.edit.name')}
                name='name'
                placeholder={t('token.edit.name_placeholder')}
                onChange={handleInputChange}
                value={name}
                autoComplete='new-password'
                required={!isEdit}
              />
            </Form.Field>
            <Form.Field>
              <Form.Input
                label='项目编号'
                name='project_number'
                placeholder='请输入项目编号（可选）'
                onChange={handleInputChange}
                value={project_number}
                autoComplete='new-password'
              />
            </Form.Field>
            <Form.Field>
              <Form.Dropdown
                label={t('token.edit.models')}
                placeholder={t('token.edit.models_placeholder')}
                name='models'
                fluid
                multiple
                search
                onLabelClick={(e, { value }) => {
                  copy(value).then();
                }}
                selection
                onChange={handleInputChange}
                value={inputs.models}
                autoComplete='new-password'
                options={modelOptions}
              />
            </Form.Field>
            <Form.Field>
              <Form.Select
                label={t('token.edit.channel_filter.mode')}
                name='channel_filter_mode'
                options={[
                  { key: 'off', text: t('token.edit.channel_filter.modes.off'), value: 'off' },
                  { key: 'whitelist', text: t('token.edit.channel_filter.modes.whitelist'), value: 'whitelist' },
                  { key: 'blacklist', text: t('token.edit.channel_filter.modes.blacklist'), value: 'blacklist' },
                ]}
                onChange={handleInputChange}
                value={inputs.channel_filter_mode}
              />
            </Form.Field>
            {inputs.channel_filter_mode !== 'off' && (
              <Form.Field>
                <Form.Dropdown
                  label={t('token.edit.channel_filter.values')}
                  placeholder={t('token.edit.channel_filter.values_placeholder')}
                  name='channel_filter_values'
                  fluid
                  multiple
                  search
                  selection
                  onChange={handleInputChange}
                  value={inputs.channel_filter_values}
                  options={channelOptions}
                />
                <Message size='small'>
                  {inputs.channel_filter_mode === 'whitelist'
                    ? t('token.edit.channel_filter.whitelist_help')
                    : t('token.edit.channel_filter.blacklist_help')}
                </Message>
              </Form.Field>
            )}
            <Form.Field>
              <Form.Input
                label={t('token.edit.ip_limit')}
                name='subnet'
                placeholder={t('token.edit.ip_limit_placeholder')}
                onChange={handleInputChange}
                value={inputs.subnet}
                autoComplete='new-password'
              />
            </Form.Field>
            <Form.Field>
              <Form.Input
                label={t('token.edit.expire_time')}
                name='expired_time'
                placeholder={t('token.edit.expire_time_placeholder')}
                onChange={handleInputChange}
                value={expired_time}
                autoComplete='new-password'
                type='datetime-local'
              />
            </Form.Field>
            <div style={{ lineHeight: '40px' }}>
              <Button
                type={'button'}
                onClick={() => {
                  setExpiredTime(0, 0, 0, 0);
                }}
              >
                {t('token.edit.buttons.never_expire')}
              </Button>
              <Button
                type={'button'}
                onClick={() => {
                  setExpiredTime(1, 0, 0, 0);
                }}
              >
                {t('token.edit.buttons.expire_1_month')}
              </Button>
              <Button
                type={'button'}
                onClick={() => {
                  setExpiredTime(0, 1, 0, 0);
                }}
              >
                {t('token.edit.buttons.expire_1_day')}
              </Button>
              <Button
                type={'button'}
                onClick={() => {
                  setExpiredTime(0, 0, 1, 0);
                }}
              >
                {t('token.edit.buttons.expire_1_hour')}
              </Button>
              <Button
                type={'button'}
                onClick={() => {
                  setExpiredTime(0, 0, 0, 1);
                }}
              >
                {t('token.edit.buttons.expire_1_minute')}
              </Button>
            </div>

            {/* 模型并发限制 */}
            <Message>模型并发限制（可选，限制该令牌对特定模型的并发请求数）</Message>
            {normalizeConcurrencyLimits(inputs.model_concurrency_limits).map((rule, index) => (
              <Form.Group key={index} widths='equal'>
                <Form.Field>
                  <Form.Select
                    label='任务类型'
                    options={TASK_TYPE_OPTIONS}
                    value={rule.task_type}
                    onChange={(e, { value }) =>
                      updateConcurrencyRule(index, 'task_type', value)
                    }
                  />
                </Form.Field>
                <Form.Field>
                  <Form.Dropdown
                    label='模型'
                    placeholder='选择或输入模型名称'
                    search
                    selection
                    allowAdditions
                    options={modelOptions}
                    value={rule.model}
                    onChange={(e, { value }) =>
                      updateConcurrencyRule(index, 'model', value)
                    }
                    onAddItem={(e, { value }) => {
                      setModelOptions((prev) => [
                        ...prev,
                        { key: value, text: value, value: value },
                      ]);
                    }}
                  />
                </Form.Field>
                <Form.Field>
                  <Form.Input
                    label='并发上限'
                    type='number'
                    min={1}
                    value={rule.limit}
                    onChange={(e, { value }) =>
                      updateConcurrencyRule(index, 'limit', value)
                    }
                  />
                </Form.Field>
                <Form.Field style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <Button
                    type='button'
                    icon
                    negative
                    size='small'
                    onClick={() => removeConcurrencyRule(index)}
                  >
                    <Icon name='trash' />
                  </Button>
                </Form.Field>
              </Form.Group>
            ))}
            <Button
              type='button'
              size='small'
              onClick={addConcurrencyRule}
              style={{ marginBottom: '1em' }}
            >
              <Icon name='plus' /> 添加并发规则
            </Button>

            <Message>双币种额度设置（留空表示不限制该币种）</Message>
            <Form.Group widths='equal'>
              <Form.Field>
                <Form.Input
                  label='美元额度限制 (USD)'
                  name='remain_quota_usd'
                  placeholder='留空表示不限制，例如：10.0000'
                  onChange={handleInputChange}
                  value={remain_quota_usd || ''}
                  autoComplete='new-password'
                  type='number'
                  step='0.0001'
                  disabled={unlimited_quota}
                />
              </Form.Field>
              <Form.Field>
                <Form.Input
                  label='人民币额度限制 (CNY)'
                  name='remain_quota_cny'
                  placeholder='留空表示不限制，例如：100.00'
                  onChange={handleInputChange}
                  value={remain_quota_cny || ''}
                  autoComplete='new-password'
                  type='number'
                  step='0.01'
                  disabled={unlimited_quota}
                />
              </Form.Field>
            </Form.Group>
            <Message>{t('token.edit.quota_notice')}</Message>
            <Form.Field>
              <Form.Input
                label={`${t('token.edit.quota')}（旧版配额，已弃用）${renderQuotaWithPrompt(
                  remain_quota,
                  t
                )}`}
                name='remain_quota'
                placeholder={t('token.edit.quota_placeholder')}
                onChange={handleInputChange}
                value={remain_quota}
                autoComplete='new-password'
                type='number'
                disabled={unlimited_quota}
              />
            </Form.Field>
            <Button
              type={'button'}
              onClick={() => {
                setUnlimitedQuota();
              }}
            >
              {unlimited_quota
                ? t('token.edit.buttons.cancel_unlimited')
                : t('token.edit.buttons.unlimited_quota')}
            </Button>
            <Button floated='right' positive onClick={submit}>
              {t('token.edit.buttons.submit')}
            </Button>
            <Button floated='right' onClick={handleCancel}>
              {t('token.edit.buttons.cancel')}
            </Button>
          </Form>
        </Card.Content>
      </Card>
    </div>
  );
};

export default EditToken;
